import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { bearingDeg, haversineKm, projectOnPolyline, nextTurnAhead } from "@/lib/navigation";
import { startWatch, requestLocationPermission, isNativePlatform } from "@/lib/geolocationAdapter";
import { KeepAwake } from "@capacitor-community/keep-awake";

/**
 * Live GPS tracking hook — Track Following mode.
 *
 * Pure track-following: no auto re-routing, no off-route detection and no
 * voice/audio cues. The hook only snaps the GPS position onto the planned
 * polyline, tracks distance/time stats and advances routeProgressIdx.
 */
export function useTracking({ route, turnPoints, vehicle, lang, voiceOn, speak, onStop, onLivePosition }) {
  const [tracking, setTracking] = useState(false);
  const [live, setLive] = useState(null); // {lat, lng, heading, accuracy}
  const [trackStats, setTrackStats] = useState({ km: 0, seconds: 0 });
  const [movingNow, setMovingNow] = useState(true);
  const [nearTurn, setNearTurn] = useState(null);
  const [snapHeading, setSnapHeading] = useState(null);
  const [speedKmh, setSpeedKmh] = useState(0);
  const [currentTurnIdx, setCurrentTurnIdx] = useState(0);
  // How far along the master polyline the rider has progressed (forward-only
  // vertex index, mirrors currentProjIdxRef). MapView/Dashboard use this to
  // trim the already-ridden portion of the route line off the display.
  const [routeProgressIdx, setRouteProgressIdx] = useState(0);

  const watcherRef = useRef(null); // { stop: () => Promise<void>, backend }
  const wasMovingRef = useRef(true);
  const hasStartedRef = useRef(false);
  const overFiveSinceRef = useRef(null);
  const underTwoSinceRef = useRef(null);
  const lastLivePosRef = useRef(null);
  const currentProjIdxRef = useRef(0); // forward-only projection index
  const lastWeakSignalToastRef = useRef(0); // throttle transient GPS-error toasts

  const routeRef = useRef(route);
  const turnPointsRef = useRef(turnPoints);
  useEffect(() => { routeRef.current = route; }, [route]);
  useEffect(() => { turnPointsRef.current = turnPoints; }, [turnPoints]);

  const startTracking = async () => {
    if (!route) {
      toast.error(lang === "en" ? "Generate a route first" : "Genera prima un percorso");
      return;
    }

    // Native permission request (no-op on web).
    const perm = await requestLocationPermission();
    if (!perm.granted && isNativePlatform()) {
      toast.error(lang === "en" ? "Location permission denied" : "Permesso posizione negato");
      return;
    }
    if (!isNativePlatform() && !navigator.geolocation) {
      toast.error(lang === "en" ? "GPS unavailable" : "GPS non disponibile");
      return;
    }

    // Keep screen awake during navigation (native only).
    if (isNativePlatform()) {
      try { await KeepAwake.keepAwake(); } catch { /* silent */ }
    }

    setTrackStats({ km: 0, seconds: 0 });
    setCurrentTurnIdx(0);
    setRouteProgressIdx(0);
    // Start in "not moving" state so the distance accumulator stays gated
    // until the pause/resume state machine actually observes real motion.
    wasMovingRef.current = false;
    hasStartedRef.current = false;
    overFiveSinceRef.current = null;
    underTwoSinceRef.current = null;
    lastLivePosRef.current = null;
    currentProjIdxRef.current = 0;
    setMovingNow(false);

    const onFix = (fix) => {
        const now = Date.now();
        const rawLat = fix.latitude;
        const rawLng = fix.longitude;
        const rawHeading = fix.heading != null ? fix.heading : undefined;
        const speedMs = fix.speed;
        const kmh = speedMs != null && !Number.isNaN(speedMs) ? speedMs * 3.6 : null;
        if (kmh != null) setSpeedKmh(kmh);

        // ---- Snap-to-route: project raw GPS onto the polyline ----
        const currentRoute = routeRef.current;
        let snappedLat = rawLat;
        let snappedLng = rawLng;
        let snapBearing = null;
        let offRouteM = 0;
        let projIdx = 0;
        if (currentRoute?.polyline && currentRoute.polyline.length >= 2) {
          const proj = projectOnPolyline([rawLat, rawLng], currentRoute.polyline);
          offRouteM = proj.distanceKm * 1000;
          projIdx = proj.index;
          // Only advance the forward-projection index — never let it go
          // backwards (prevents forced U-turns when GPS jitters at a Y-junction).
          // Also sanity-cap how far it's allowed to jump in one tick: on a
          // LOOP route the last polyline vertex sits almost on top of the
          // first one, so a GPS fix taken right at the start can nearest-
          // match either end. Without this guard that ambiguity can jump
          // straight to the end of the route on the very first fix, which
          // (via routeProgressIdx) trims the entire line off the map
          // instantly. A real fix-to-fix advance covers at most a couple
          // hundred metres of route distance; a wrap-to-the-end jump is
          // the whole route, so a 500 m cap cleanly tells them apart.
          if (projIdx > currentProjIdxRef.current) {
            let jumpM = 0;
            for (let i = currentProjIdxRef.current; i < projIdx && jumpM <= 500; i++) {
              jumpM += haversineKm(currentRoute.polyline[i], currentRoute.polyline[i + 1]) * 1000;
            }
            if (jumpM <= 500) {
              currentProjIdxRef.current = projIdx;
              setRouteProgressIdx(projIdx);
            }
          }
          // Snap only when we're within a reasonable distance of the route
          // (60m). Beyond that we don't want to teleport the arrow — let
          // the raw GPS position show the deviation so the rider can see
          // they're off track.
          if (offRouteM <= 60) {
            const nearest = currentRoute.polyline[proj.index];
            snappedLat = nearest[0];
            snappedLng = nearest[1];
          }
          const nextIdx = Math.min(proj.index + 1, currentRoute.polyline.length - 1);
          if (nextIdx > proj.index) {
            // NOTE: this is the direction the ROUTE goes at the nearest
            // point, not necessarily the direction the rider is actually
            // moving. Only used as a last-resort fallback below — using it
            // as the primary heading made the arrow/compass point the way
            // the route goes even off-route or mid-U-turn, ignoring the
            // rider's real movement.
            snapBearing = bearingDeg(
              currentRoute.polyline[proj.index],
              currentRoute.polyline[nextIdx]
            );
          }
        }

        // ---- Pause / Resume state machine ----
        if (kmh != null) {
          if (!hasStartedRef.current) {
            if (kmh > 5) {
              hasStartedRef.current = true;
              overFiveSinceRef.current = now;
            }
          } else {
            if (kmh > 5) {
              if (overFiveSinceRef.current == null) overFiveSinceRef.current = now;
              underTwoSinceRef.current = null;
              if (now - overFiveSinceRef.current >= 2000 && !wasMovingRef.current) {
                setMovingNow(true);
                wasMovingRef.current = true;
              }
            } else if (kmh < 2) {
              if (underTwoSinceRef.current == null) underTwoSinceRef.current = now;
              overFiveSinceRef.current = null;
              if (now - underTwoSinceRef.current >= 15000 && wasMovingRef.current) {
                setMovingNow(false);
                wasMovingRef.current = false;
              }
            } else {
              overFiveSinceRef.current = null;
              underTwoSinceRef.current = null;
            }
          }
        }

        // ---- Distance accumulator (uses RAW GPS to stay physically accurate).
        //      Threshold 2m per-tick — filters GPS jitter but preserves the
        //      real distance of a slow crawl. `lastLivePosRef` is advanced
        //      EVERY tick that we accept a delta, ensuring the counter
        //      keeps up with real movement (the previous 5m threshold was
        //      too coarse: a moto lapping a slow tornante at 5 km/h ticks
        //      every ~1.4m and would never accumulate).
        //
        //      User directive (2026-07-30): standing-still fixes were
        //      leaking into the counter because the 2m floor alone can't
        //      tell "rider crawling at 5 km/h" from "rider parked but
        //      GPS jittering by 6-10m per tick". Gate:
        //        - `wasMovingRef.current` must be true (pause/resume
        //          state machine 187-213 already tracks this from kmh)
        //        - fix.accuracy must be < 25m (a fix reporting 40-80m
        //          accuracy is almost always jitter in tunnel/urban
        //          canyon, never a real crawl)
        //      Both conditions must hold. GPS-speed being null is NOT a
        //      criterion — many phones report speed=null during real
        //      motion.
        const prevRaw = lastLivePosRef.current;
        // Real, OBSERVED direction of travel — computed straight from the
        // last two raw fixes. This is what fixes the arrow/compass pointing
        // the wrong way: it reflects where the rider actually just went,
        // correct even off-route or backtracking, unlike the route-implied
        // `snapBearing` above. Below ~3m the delta is dominated by ordinary
        // GPS jitter, so it's ignored (falls through to the next source).
        let movementHeading = null;
        const accuracyOk = fix.accuracy == null || fix.accuracy < 25;
        const distanceAllowed = wasMovingRef.current && accuracyOk;
        if (prevRaw) {
          const km = haversineKm([prevRaw.lat, prevRaw.lng], [rawLat, rawLng]);
          if (km >= 0.003 && km < 0.5) {
            movementHeading = bearingDeg([prevRaw.lat, prevRaw.lng], [rawLat, rawLng]);
          }
          if (km >= 0.002 && km < 0.5 && distanceAllowed) {
            // 500m sanity ceiling — rejects GPS teleports (tunnel exit etc)
            setTrackStats((s) => ({ ...s, km: s.km + km }));
            lastLivePosRef.current = { lat: rawLat, lng: rawLng };
          } else if (km >= 0.5) {
            // Big jump: don't accumulate but re-anchor so we don't stack it
            // onto the next legitimate delta.
            lastLivePosRef.current = { lat: rawLat, lng: rawLng };
          } else if (km >= 0.002 && km < 0.5) {
            // Movement observed but we're either paused (kmh state
            // machine) or the fix is too noisy — re-anchor so the
            // next accepted delta measures from HERE, not from the
            // stale pre-jitter anchor (otherwise a legitimate crawl
            // after a pause double-counts the intervening drift).
            lastLivePosRef.current = { lat: rawLat, lng: rawLng };
          }
        } else {
          lastLivePosRef.current = { lat: rawLat, lng: rawLng };
        }

        // Heading priority: observed movement first (what the rider is
        // ACTUALLY doing) → device-reported heading → route-implied bearing
        // as a last resort (only relevant on the very first fix, before any
        // movement has been recorded yet).
        const effectiveHeading =
          movementHeading != null ? movementHeading : rawHeading != null ? rawHeading : snapBearing;
        if (effectiveHeading != null) setSnapHeading(effectiveHeading);

        // ---- Emit snapped live position ----
        const snapped = {
          lat: snappedLat,
          lng: snappedLng,
          heading: effectiveHeading,
          accuracy: fix.accuracy,
          rawLat,
          rawLng,
        };
        setLive(snapped);
        onLivePosition?.({ lat: snappedLat, lng: snappedLng });

        // ---- Turn detection (visual only, no voice) ----
        const currentTurns = turnPointsRef.current;
        if (currentRoute?.polyline && currentTurns?.length) {
          const t = nextTurnAhead([rawLat, rawLng], currentRoute.polyline, currentTurns);
          if (t) {
            // Update currentTurnIdx: find its position in the turns array.
            const idx = currentTurns.findIndex((x) => x.index === t.index);
            if (idx >= 0) setCurrentTurnIdx(idx);
          }
          if (t && t.distanceKm * 1000 <= 200) {
            setNearTurn(t);
          } else {
            setNearTurn(null);
          }
        }
    };

    const onErr = (err) => {
      // Only permanent errors (permission denied) tear down tracking.
      // Transient errors (POSITION_UNAVAILABLE, TIMEOUT) are just
      // signal hiccups — watchPosition keeps polling on its own, so we
      // must NOT stop the whole session. A soft "weak signal" toast
      // once every ~15s is enough for the rider.
      if (err?.fatal || err?.code === 'PERMISSION_DENIED') {
        toast.error(
          (lang === "en" ? "GPS permission denied" : "Permesso GPS negato")
        );
        stopTracking('error-fatal');
        return;
      }
      // Transient — throttle to avoid toast spam on continuously flaky GPS.
      const now = Date.now();
      if (now - (lastWeakSignalToastRef.current || 0) > 15000) {
        lastWeakSignalToastRef.current = now;
        toast(
          lang === "en" ? "Weak GPS signal" : "Segnale GPS debole",
          { icon: "📡" }
        );
      }
    };

    try {
      watcherRef.current = await startWatch(onFix, onErr, {
        distanceFilterM: 1,
        backgroundTitle: lang === "en" ? "Brema ValliRider" : "Brema ValliRider",
        backgroundMessage:
          lang === "en"
            ? "GPS tracking active — keep the app open for navigation."
            : "Tracciamento GPS attivo — mantieni l'app aperta per la navigazione.",
      });
    } catch (e) {
      toast.error((lang === "en" ? "GPS start failed: " : "Avvio GPS fallito: ") + (e?.message || e));
      return;
    }

    setTracking(true);
    // iOS Shake-to-Undo: blur any active input.
    try {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    } catch { /* ignore */ }
    toast.success(lang === "en" ? "Live tracking on" : "Live tracking attivo");
  };

  const stopTracking = (reason = 'user') => {
    if (watcherRef.current?.stop) {
      // fire-and-forget; native stop is async but we don't want to
      // block the UI on it.
      watcherRef.current.stop().catch(() => {});
    }
    watcherRef.current = null;
    // Release the wake lock (native only — no-op on web).
    if (isNativePlatform()) {
      KeepAwake.allowSleep().catch(() => {});
    }
    setTracking(false);
    setNearTurn(null);
    setMovingNow(false);
    setSpeedKmh(0);
    setCurrentTurnIdx(0);
    setRouteProgressIdx(0);
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    // Only user-initiated stops wipe the parent's route/dest state.
    // Fatal GPS errors (permission denied) preserve the generated route
    // so the rider can grant permission and hit AVVIA PERCORSO again
    // without losing the ride they built.
    if (reason === 'user') {
      onStop?.();
    }
    setLive(null);
  };

  // iOS Shake-to-Undo hardening while tracking.
  //
  // User directive (2026-07-30): SKIP editable elements so the
  // destination search / voice-transcript inputs remain typable
  // while the ride is live. The AppDelegate-level
  // `applicationSupportsShakeToEdit = false` already neutralises the
  // Undo gesture natively; a JS focusin blur is only needed as a
  // secondary belt-and-braces for any stray shake events that reach
  // the WebView from a plugin, and it MUST NOT touch INPUT /
  // TEXTAREA / contentEditable, which are the fields the rider
  // legitimately wants to focus.
  useEffect(() => {
    if (!tracking) return;
    const onFocusIn = (e) => {
      const el = e.target;
      if (!el) return;
      const tag = (el.tagName || "").toUpperCase();
      // Explicitly PRESERVE focus on editable fields — the whole
      // point of the fix is that these must remain typable.
      if (tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable) {
        return;
      }
      // Non-editable element grabbed focus (e.g. a stray tap on a
      // decorative element, an autofill hint). Strip focus silently.
      try { el.blur(); } catch { /* ignore */ }
    };
    document.addEventListener("focusin", onFocusIn, true);
    // Do NOT preemptively blur `document.activeElement` here either
    // — it would rip focus off the destination search the moment
    // tracking is toggled on with the field already active.
    return () => document.removeEventListener("focusin", onFocusIn, true);
  }, [tracking]);

  // Tick elapsed seconds — auto-pause when stationary
  useEffect(() => {
    if (!tracking) return;
    const iv = setInterval(() => {
      if (movingNow) {
        setTrackStats((s) => ({ ...s, seconds: s.seconds + 1 }));
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [tracking, movingNow]);

  // Cleanup watch on unmount
  useEffect(() => () => {
    if (watcherRef.current?.stop) {
      watcherRef.current.stop().catch(() => {});
    }
    if (isNativePlatform()) {
      KeepAwake.allowSleep().catch(() => {});
    }
  }, []);

  return {
    tracking,
    live,
    trackStats,
    movingNow,
    nearTurn,
    snapHeading,
    rejoinPolyline: null,
    speedKmh,
    currentTurnIdx,
    routeProgressIdx,
    startTracking,
    stopTracking,
  };
}
